import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useMediaQuery, useTheme } from "@mui/material";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Snackbar,
    Divider,
    FormControlLabel,
    Switch,
    Autocomplete,
    Chip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Radio,
    Stack,
    IconButton,
} from "@mui/material";
import { Save, ArrowBack, InsertDriveFile, PictureAsPdf, Article, TableChart, Image, Folder } from "@mui/icons-material";
import { useAuth } from "../../../../auth";
import { useMutation, useQuery } from "@tanstack/react-query";
import { customInstanceDocumentos } from "../../../../api/axios-client";
import { useFindDocument as useGetDocument, useListArchivalClassification as useListArchivalClassifications, useListSubDocumentType as useListSubDocumentTypes, listDigitalFile as listDocumentFiles } from "../../../../api/documentos/endpoints";
import { FolderTree } from "../../../../components/FolderTree";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/__auth/documents/$documentId/edit")({
    component: EditDocumentPage,
    staticData: { title: "routes.editDocument" },
});

function FileIcon({ name }: { name: string }) {
    const ext = (name || "").split(".").pop()?.toLowerCase();
    if (ext === "pdf") return <PictureAsPdf color="error" fontSize="small" />;
    if (["doc", "docx"].includes(ext || "")) return <Article color="primary" fontSize="small" />;
    if (["xls", "xlsx", "csv"].includes(ext || "")) return <TableChart color="success" fontSize="small" />;
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext || "")) return <Image color="secondary" fontSize="small" />;
    return <InsertDriveFile fontSize="small" />;
}

interface FileEntry {
    fileId: string;
    fileName: string;
    title: string;
    extension: any;
}

interface EditForm {
    name: string;
    documentType: any;
    archivalClassification: any;
    motivoReclassificacao: string;
    author: string;
    creationDate: string;
    physicalFileExists: boolean;
    containsPersonalData: boolean;
    containsSensitiveData: boolean;
    mainFileId: string;
    fileEntries: FileEntry[];
}

function EditDocumentPage() {
    const { t } = useTranslation();
    const { documentId } = Route.useParams();
    const { user } = useAuth();
    const tenantId = user?.tenant || "default-domain";
    const docId = Number(documentId);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [feedback, setFeedback] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
        open: false, message: "", severity: "success",
    });

    const [selectedFolder, setSelectedFolder] = useState<{ id: number; name: string; workspaceId?: number } | null>(null);

    const { data: documentData, isLoading: isDocLoading, isError: isDocError } = useGetDocument(
        tenantId, docId, { query: { enabled: !!documentId } }
    );

    const { data: filesData, isLoading: isFilesLoading } = useQuery({
        queryKey: ["document-files", tenantId, docId],
        queryFn: () => listDocumentFiles(tenantId, docId, { } as any),
        enabled: !!documentId,
    });

    const { data: classificationsData } = useListArchivalClassifications(tenantId, {
            page: 0,
            size: 100
        } as any);

    const { data: typesData } = useListSubDocumentTypes(tenantId, {
            page: 0,
            size: 100,
            enabled: true
        } as any, { query: { enabled: true } });

    const doc = documentData as any;
    const files: any[] = Array.isArray(filesData) ? filesData : (filesData as any)?.data || (filesData as any)?.content || [];
    const classifications = (classificationsData as any)?.content || [];
    const types = (typesData as any)?.content || [];

    const isLoading = isDocLoading || isFilesLoading;

    const editMutation = useMutation({
        mutationFn: async (payload: { document: string }) => {
            const formData = new FormData();
            formData.append("document", payload.document);
            return customInstanceDocumentos(`/${tenantId}/documents/${docId}`, {
                method: "PUT",
                body: formData as any,
            });
        },
    });

    const form = useForm<EditForm>({
        values: doc ? {
            name: doc.name || "",
            documentType: doc.subDocumentType || null,
            archivalClassification: doc.archivalClassification || null,
            motivoReclassificacao: doc.motivoReclassificacao || "",
            author: doc.properties?.author || "",
            creationDate: doc.properties?.creationDate || "",
            physicalFileExists: doc.properties?.physicalFileExists ?? false,
            containsPersonalData: doc.properties?.containsPersonalData ?? false,
            containsSensitiveData: doc.properties?.containsSensitiveData ?? false,
            mainFileId: files.find((f: any) => f.isMainFile)?.id || files[0]?.id || "",
            fileEntries: files.map((f: any) => ({
                fileId: f.id,
                fileName: f.fileName || "",
                title: f.title || f.fileName || "",
                extension: f.extension || null,
            })),
        } : undefined,
    });

    const { register, handleSubmit, control } = form;
    const { fields: fileFields } = useFieldArray({ control, name: "fileEntries" });

    if (doc && !selectedFolder) {
        setSelectedFolder({
            id: doc.parent?.id,
            name: doc.parent?.name || "",
            workspaceId: doc.workspace?.id,
        });
    }

    const handleFolderSelect = (node: { id: number; name: string; type: "workspace" | "folder"; workspaceId?: number; rootId: number }) => {
        if (node.type === "workspace") {
            setSelectedFolder({ id: node.id, name: node.name, workspaceId: node.id });
        } else {
            setSelectedFolder({ id: node.id, name: node.name, workspaceId: node.workspaceId || node.rootId });
        }
    };

    const onSubmit = (data: EditForm) => {
        const workspaceId = selectedFolder?.workspaceId || doc?.workspace?.id;
        const parentId = selectedFolder?.id || doc?.parent?.id;

        const documentPayload = {
            name: data.name,
            description: data.name,
            subDocumentType: data.documentType ? { id: data.documentType.id } : undefined,
            archivalClassification: data.archivalClassification ? { id: data.archivalClassification.id } : undefined,
            motivoReclassificacao: data.motivoReclassificacao || undefined,
            properties: {
                author: data.author,
                creationDate: data.creationDate,
                physicalFileExists: data.physicalFileExists,
                containsPersonalData: data.containsPersonalData,
                containsSensitiveData: data.containsSensitiveData,
            },
            workspace: { id: workspaceId },
            parent: { id: parentId },
            collaborators: doc?.collaborators || [],
            files: data.fileEntries.map((entry, index) => ({
                id: entry.fileId,
                name: entry.title,
                fileName: entry.fileName,
                title: entry.title,
                extension: entry.extension ? { id: entry.extension.id, name: entry.extension.name } : undefined,
                genre: { id: 1, name: "text" },
                isScanDoc: false,
                isMainFile: entry.fileId === data.mainFileId,
                displayOrder: index,
            })),
        };

        editMutation.mutate({ document: JSON.stringify(documentPayload) }, {
            onSuccess: () => {
                setFeedback({ open: true, message: t("documents.editSuccess"), severity: "success" });
                setTimeout(() => window.history.back(), 1500);
            },
            onError: (error: any) => {
                const msg = error?.response?.data?.userMessage || error?.response?.data?.message || t("documents.editError");
                setFeedback({ open: true, message: msg, severity: "error" });
            },
        });
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 2 }}>
                <CircularProgress />
                <Typography color="text.secondary">{t("common.loading")}</Typography>
            </Box>
        );
    }

    if (isDocError || !doc) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{t("documents.editError")}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", p: { xs: 1, sm: 2 }, maxWidth: 900, mx: "auto" }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <IconButton onClick={() => window.history.back()} color="primary">
                    <ArrowBack />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant={isMobile ? "h6" : "h5"}>{t("documents.editDocument")}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {doc.code} | {doc.name}
                    </Typography>
                </Box>
            </Stack>

            <form onSubmit={handleSubmit(onSubmit)}>
                <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>{t("documents.sections.identification")}</Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                            label={t("documents.documentTitle")}
                            fullWidth
                            {...register("name", { required: true })}
                            InputLabelProps={{ shrink: true }}
                        />

                        <Controller
                            name="documentType"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    {...field}
                                    options={types}
                                    getOptionLabel={(option: any) => option?.name || ""}
                                    isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                                    onChange={(_, val) => field.onChange(val)}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t("documents.documentType")} InputLabelProps={{ shrink: true }} />
                                    )}
                                />
                            )}
                        />

                        <Controller
                            name="archivalClassification"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    {...field}
                                    options={classifications}
                                    getOptionLabel={(option: any) =>
                                        option ? `${option.codigoClassificacao || ""} - ${option.assunto || ""}` : ""
                                    }
                                    isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                                    onChange={(_, val) => field.onChange(val)}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t("documents.archivalClassification")} InputLabelProps={{ shrink: true }} />
                                    )}
                                />
                            )}
                        />

                        <TextField
                            label={t("documents.reclassificationReason")}
                            fullWidth
                            multiline
                            rows={2}
                            {...register("motivoReclassificacao")}
                            InputLabelProps={{ shrink: true }}
                        />

                        <Controller
                            name="physicalFileExists"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Switch checked={field.value} onChange={field.onChange} />}
                                    label={field.value ? t("documents.physical") : t("documents.digital")}
                                />
                            )}
                        />
                    </Box>
                </Paper>

                <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>{t("documents.sections.authorship")}</Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                        <TextField
                            label={t("documents.author")}
                            fullWidth
                            {...register("author")}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label={t("documents.review.creationDate")}
                            type="date"
                            fullWidth
                            {...register("creationDate")}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                </Paper>

                <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Folder fontSize="small" />
                            {t("documents.sections.storage")}
                        </Box>
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t("documents.destinationFolder")}:
                        </Typography>
                        <Chip
                            icon={<Folder />}
                            label={selectedFolder?.name || doc.parent?.name || "—"}
                            color={selectedFolder && selectedFolder.id !== doc.parent?.id ? "primary" : "default"}
                            variant={selectedFolder && selectedFolder.id !== doc.parent?.id ? "filled" : "outlined"}
                            sx={{ mb: 2 }}
                        />
                        {selectedFolder && selectedFolder.id !== doc.parent?.id && (
                            <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                                (será movido)
                            </Typography>
                        )}
                    </Box>

                    <Typography variant="caption" color="text.secondary" gutterBottom>
                        {t("documents.wizard.centralRepository")}
                    </Typography>
                    <Box sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        maxHeight: 280,
                        overflowY: 'auto',
                        mt: 1,
                    }}>
                        <FolderTree
                            tenantId={tenantId}
                            onSelect={handleFolderSelect}
                            selectedId={selectedFolder?.id || doc.parent?.id}
                            selectedType="folder"
                        />
                    </Box>
                </Paper>

                <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>{t("documents.sections.privacyAccess")}</Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1, sm: 4 } }}>
                        <Controller
                            name="containsPersonalData"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Switch checked={field.value} onChange={field.onChange} />}
                                    label={t("documents.containsPersonalData")}
                                />
                            )}
                        />
                        <Controller
                            name="containsSensitiveData"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Switch checked={field.value} onChange={field.onChange} />}
                                    label={t("documents.containsSensitiveData")}
                                />
                            )}
                        />
                    </Box>
                </Paper>

                {fileFields.length > 0 && (
                    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>{t("documents.wizard.documentFiles")}</Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Controller
                            name="mainFileId"
                            control={control}
                            render={({ field: mainFileField }) => (
                                isMobile ? (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        {fileFields.map((fileField, index) => (
                                            <Paper key={fileField.id} variant="outlined" sx={{ p: 1.5 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                    <Radio
                                                        checked={mainFileField.value === fileField.fileId}
                                                        onChange={() => mainFileField.onChange(fileField.fileId)}
                                                        size="small"
                                                    />
                                                    <FileIcon name={fileField.fileName || ""} />
                                                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                                        {fileField.fileName || "—"}
                                                    </Typography>
                                                </Box>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label={t("documents.fileTitle")}
                                                    variant="outlined"
                                                    {...register(`fileEntries.${index}.title`)}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Paper>
                                        ))}
                                    </Box>
                                ) : (
                                    <Box sx={{ overflowX: "auto" }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell width={60}>{t("documents.mainFile")}</TableCell>
                                                    <TableCell>{t("documents.file")}</TableCell>
                                                    <TableCell sx={{ minWidth: 250 }}>{t("documents.fileTitle")}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {fileFields.map((fileField, index) => (
                                                    <TableRow key={fileField.id} hover>
                                                        <TableCell>
                                                            <Radio
                                                                checked={mainFileField.value === fileField.fileId}
                                                                onChange={() => mainFileField.onChange(fileField.fileId)}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                <FileIcon name={fileField.fileName || ""} />
                                                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                                    {fileField.fileName || "—"}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                fullWidth
                                                                variant="standard"
                                                                {...register(`fileEntries.${index}.title`)}
                                                                InputProps={{ disableUnderline: false }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                )
                            )}
                        />
                    </Paper>
                )}

                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "flex-end", gap: 2, mb: 10 }}>
                    <Button
                        variant="outlined"
                        fullWidth={isMobile}
                        onClick={() => window.history.back()}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth={isMobile}
                        startIcon={editMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <Save />}
                        disabled={editMutation.isPending}
                    >
                        {editMutation.isPending ? t("common.saving") : t("common.save")}
                    </Button>
                </Box>
            </form>

            <Snackbar
                open={feedback.open}
                autoHideDuration={6000}
                onClose={() => setFeedback({ ...feedback, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setFeedback({ ...feedback, open: false })} severity={feedback.severity} sx={{ width: "100%" }}>
                    {feedback.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
